import { Alert, Code, Divider, Group, Select, Stack, Switch, Tabs, Text, TextInput, Textarea } from '@mantine/core';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import useRetrieveAutomationTestFixtures from '@/hooks/api/automations/useRetrieveAutomationTestFixtures.ts';
import useRetrieveAutomationVersionTestSuite from '@/hooks/api/automationVersions/useRetrieveAutomationVersionTestSuite.ts';
import useRetrieveAutomationVersion from '@/hooks/api/automationVersions/useRetrieveAutomationVersion.ts';
import useRetrieveAutomationReadiness from '@/hooks/api/automationVersions/useRetrieveAutomationReadiness.ts';
import useRetrieveAutomationVersionTestCaseRuns from '@/hooks/api/automationVersions/useRetrieveAutomationVersionTestCaseRuns.ts';
import useRetrieveAutomationTestFixtureInputs from '@/hooks/api/automationVersions/useRetrieveAutomationTestFixtureInputs.ts';
import useRetireAutomationTestFixture from '@/hooks/api/automations/useRetireAutomationTestFixture.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import { isVerificationSelectionChanged } from '@/services/verificationErrors.ts';
import type { VerificationPreparation, VerificationPreparationResult } from '@/types/verification.ts';
import TestPlanAnalysisSummary from './TestPlanAnalysisSummary.tsx';
import type { TestPlanAnalysis, TestPlanIssue } from '@/types/testPlanAnalysis.ts';
import { formatExecutionIntegrityDisposition } from './executionIntegrity.ts';
import {
  AutomationTestFixture,
  AutomationTestFixtureInputDiagnostic,
  AutomationTestFixtureInputOverridesPreviewResponse,
  AutomationVersionTestFixtureBinding,
  AutomationVersionSimple,
  AutomationVersionTestSuite,
  AutomationVersionTestSuiteCase,
  AutomationVersionTestSuitePreviewResponse,
  FixtureDependencyDiagnostic,
  getAutomationVersionLabel,
} from '@/types/automations.ts';
import {
  AUTOMATION_BUILDER_READINESS_KEY,
  AUTOMATION_TEST_FIXTURES_KEY,
  AUTOMATION_VERSION_TEST_CASE_RUNS_KEY,
  AUTOMATION_VERSION_TEST_SUITE_KEY,
} from '@/utils/queryKeys.ts';
import {
  create_automation_version_test_case_plan_url,
  create_automation_version_test_case_run_from_plan_url,
  get_automation_test_fixtures_url,
  get_automation_version_test_fixture_binding_url,
  get_automation_version_test_suite_url,
  preview_automation_test_fixture_inputs_url,
  preview_automation_version_test_suite_url,
} from '@/utils/urls.ts';
import SyntheticFixtureAuthoringForm from './SyntheticFixtureAuthoringForm.tsx';
import DiagnosticRunPanel from './DiagnosticRunPanel.tsx';
import VerificationPanel from './VerificationPanel.tsx';
import {
  bindFixtureSubstitutions,
  buildVersionTestSuiteSnapshot,
  createVersionTestCaseDraft,
  EMPTY_VERSION_TEST_SUITE_SNAPSHOT,
  parseVersionTestSuiteSnapshot,
  toVersionTestCaseDraft,
  type VersionTestCaseDraft,
} from './versionTestSuiteAuthoring.ts';
import { parseSyntheticFixtureOverrides } from './syntheticFixtureAuthoring.ts';
import FixtureDependencyDiagnostics from './FixtureDependencyDiagnostics.tsx';
import classes from './VersionTestSuiteModal.module.css';

type Props = {
  opened: boolean;
  onClose: () => void;
  version: AutomationVersionSimple;
};

function derivedInputOriginLabel(input: NonNullable<AutomationTestFixture['derivation_input_manifest']>[number]) {
  const { origin, fixture_mode: mode } = input.evidence.origin;
  switch (origin) {
    case 'provided': return 'provided';
    case 'synthetic': return 'synthetic';
    case 'supplied': return 'supplied text';
    case 'source_run': return 'source run';
  }
  switch (mode) {
    case 'provided_file_seed': return input.evidence.kind === 'text' ? 'supplied text' : 'provided';
    case 'synthetic_file_seed':
    case 'synthetic_trigger_seed': return 'synthetic';
    case 'source_lineage': return 'source run';
    case 'source_lineage_with_input_overrides':
      return input.binding.kind === 'input' ? 'synthetic' : 'origin unavailable';
    default: return 'origin unavailable';
  }
}

function requiresFixtureReadiness(fixture: AutomationTestFixture) {
  return Boolean(fixture.parent_fixture_id) || fixture.mode === 'provided_file_seed' || fixture.mode === 'synthetic_file_seed' || fixture.mode === 'trigger_input_bundle';
}

function inputFixtureReadiness(fixture: AutomationTestFixture) {
  if (fixture.parent_fixture_id) {
    if (fixture.fixture_processing_status === 'failed') return 'failed';
    return fixture.derivation_input_manifest?.length ? fixture.fixture_processing_status ?? 'unavailable' : 'unavailable';
  }
  if (fixture.mode === 'trigger_input_bundle') {
    return fixture.trigger_input_manifest?.length ? fixture.fixture_processing_status ?? 'unavailable' : 'unavailable';
  }
  if (fixture.fixture_processing_status) {
    return fixture.fixture_processing_status;
  }
  // Older file-only responses have no aggregate status. A companion bundle
  // without that status cannot be admitted by this client.
  return fixture.text_seed_manifest?.length ? 'unavailable' : fixture.file_seed_manifest?.dataset_processing_status ?? 'unavailable';
}

function inputFixtureLabel(fixture: AutomationTestFixture) {
  if (fixture.parent_fixture_id) {
    return `Derived inputs · ${fixture.derivation_input_manifest?.length ?? 0} retained · ${inputFixtureReadiness(fixture)}`;
  }
  if (fixture.mode === 'trigger_input_bundle') {
    const inputs = fixture.trigger_input_manifest ?? [];
    const kinds = [...new Set(inputs.map((input) => input.kind))].join('/');
    const origins = [...new Set(inputs.map((input) => input.origin))].join('/');
    return `Input bundle · ${inputs.length} ${kinds} input${inputs.length === 1 ? '' : 's'} · ${origins} · ${inputFixtureReadiness(fixture)}`;
  }
  const textCount = fixture.text_seed_manifest?.length ?? 0;
  const status = inputFixtureReadiness(fixture);
  const origin = fixture.mode === 'provided_file_seed' ? 'Provided original file' : 'Synthetic file';
  return textCount
    ? `${origin} + ${textCount} supplied text input${textCount === 1 ? '' : 's'} · ${status}`
    : `${origin} · ${status}`;
}

type CaseDefinition = Pick<AutomationVersionTestSuiteCase, 'id' | 'name' | 'description' | 'intent'>;
type FixtureMode = 'source_lineage' | 'source_lineage_with_input_overrides' | 'synthetic_trigger_seed';
type PlanPreview = {
  plan_token?: string | null; eligible: boolean;
  execution_slice?: TestPlanAnalysis & {
    scheduled_step_ids: string[];
    blocked_effect_step_ids: string[];
    fixture_source_lineage_sha256?: string | null;
    fixture_definition_sha256?: string | null;
  } | null;
  issues?: (TestPlanIssue & { reason_code: string })[];
  diagnostics?: FixtureDependencyDiagnostic[];
};
type PendingLaunch = { plan: PlanPreview; requestId: string; runId?: string };

function pendingLaunchStorageKey(automationId: string, versionId: string, suiteId: string) {
  return `builder-test-launch:${automationId}:${versionId}:${suiteId}`;
}

function readPendingLaunches(key: string): Record<string, PendingLaunch> {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(key) ?? '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(Object.entries(stored).filter((entry): entry is [string, PendingLaunch] => {
      const launch = entry[1] as PendingLaunch | null;
      return Boolean(launch && typeof launch.requestId === 'string'
        && launch.plan?.eligible === true && typeof launch.plan.plan_token === 'string');
    }));
  } catch {
    return {};
  }
}

function writePendingLaunches(key: string, launches: Record<string, PendingLaunch>) {
  try {
    if (Object.keys(launches).length) sessionStorage.setItem(key, JSON.stringify(launches));
    else sessionStorage.removeItem(key);
  } catch {
    // A private or storage-disabled browser still supports same-mount retries.
  }
}

const CaseHistory = ({ versionId, caseId }: { versionId: string; caseId: string }) => {
  const history = useRetrieveAutomationVersionTestCaseRuns(versionId, caseId);
  if (history.isError) {
    return <Text size="xs" c="red">Recent regression-run evidence could not be loaded.</Text>;
  }
  if (!history.data?.runs.length) return null;
  return <Stack gap={1}>
    <Text size="xs" c="dimmed">Recent regression-run evidence</Text>
    {history.data.runs.slice(0, 3).map((run) => (
      <Text key={run.run_id} size="xs">
        <Code>{run.run_id}</Code> · Runtime: {run.run_status.replaceAll('_', ' ')} · {new Date(run.created_at).toLocaleString()}
      </Text>
    ))}
  </Stack>;
};

export default function VersionTestSuiteModal({
  opened, onClose, version,
}: Props) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const automationId = useAutomationId();
  const suiteQuery = useRetrieveAutomationVersionTestSuite(version.id, opened);
  const suiteRevisionId = suiteQuery.data?.suite_id ?? null;
  const selectionGeneration = suiteQuery.data?.selection_generation ?? 0;
  const activeSaveScope = useRef({ automationId, versionId: version.id, opened, suiteRevisionId, selectionGeneration });
  useEffect(() => {
    activeSaveScope.current = { automationId, versionId: version.id, opened, suiteRevisionId, selectionGeneration };
  }, [automationId, version.id, opened, suiteRevisionId, selectionGeneration]);
  const suiteKey = `${version.id}:${suiteRevisionId ?? "unselected"}:${selectionGeneration}`;
  const launchStorageKey = pendingLaunchStorageKey(automationId, version.id, suiteQuery.data?.suite_id ?? "unselected");
  const exactVersion = useRetrieveAutomationVersion(opened ? version.id : undefined);
  const routeInspected = exactVersion.data?.id === version.id && !exactVersion.isError;
  const readinessQuery = useRetrieveAutomationReadiness(version.id, opened);
  const fixturesQuery = useRetrieveAutomationTestFixtures(opened);
  const fixtureInputsQuery = useRetrieveAutomationTestFixtureInputs(version.id, opened);
  const retireFixture = useRetireAutomationTestFixture(version.id);
  const testAssuranceStatus = readinessQuery.data?.test_assurance?.status;
  const { refetch: refetchReadiness } = readinessQuery;
  const [caseDrafts, setCaseDrafts] = useState<VersionTestCaseDraft[]>([]);
  const [mode, setMode] = useState<string | null>('regression');
  const [advancedSnapshotText, setAdvancedSnapshotText] = useState(JSON.stringify(EMPTY_VERSION_TEST_SUITE_SNAPSHOT, null, 2));
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [fixtureName, setFixtureName] = useState('');
  const [scenarioLabel, setScenarioLabel] = useState('');
  const [sourceRunId, setSourceRunId] = useState('');
  const [fixtureMode, setFixtureMode] = useState<FixtureMode>('source_lineage');
  const [inputOverridesText, setInputOverridesText] = useState('[]');
  const [triggerOverridesText, setTriggerOverridesText] = useState('[]');
  const [confirmSyntheticNonSensitive, setConfirmSyntheticNonSensitive] = useState(false);
  const [fixtureToRetire, setFixtureToRetire] = useState<AutomationTestFixture | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSaving(false); }, [automationId, version.id, opened, suiteRevisionId, selectionGeneration]);
  const [reviewingSuite, setReviewingSuite] = useState(false);
  const [suiteReview, setSuiteReview] = useState<{ key: string; result: AutomationVersionTestSuitePreviewResponse } | null>(null);
  const [creatingFixture, setCreatingFixture] = useState(false);
  const [preflightingFixtureInputs, setPreflightingFixtureInputs] = useState(false);
  const [casePlans, setCasePlans] = useState<Record<string, PlanPreview | undefined>>({});
  const [planningCaseId, setPlanningCaseId] = useState<string | null>(null);
  const preparationInFlight = useRef(false);
  const [runningCaseId, setRunningCaseId] = useState<string | null>(null);
  const launchInFlight = useRef(false);
  const runLaunchRequests = useRef<{ key: string; launches: Record<string, PendingLaunch> }>({
    key: launchStorageKey,
    launches: readPendingLaunches(launchStorageKey),
  });
  if (runLaunchRequests.current.key !== launchStorageKey) {
    runLaunchRequests.current = { key: launchStorageKey, launches: readPendingLaunches(launchStorageKey) };
  }
  const [suiteValidationError, setSuiteValidationError] = useState<string | null>(null);
  const [suiteInitializationError, setSuiteInitializationError] = useState<string | null>(null);
  const [loadedSuiteSnapshot, setLoadedSuiteSnapshot] = useState<NonNullable<AutomationVersionTestSuite['snapshot']> | null>(null);
  const [initializedSuiteKey, setInitializedSuiteKey] = useState<string | null>(null);
  const [bindingCaseIndex, setBindingCaseIndex] = useState<number | null>(null);
  const bindingRequestSequence = useRef(0);
  const suiteReviewKey = JSON.stringify([version.id, showAdvancedEditor, caseDrafts, advancedSnapshotText]);
  const currentSuiteReview = suiteReview?.key === suiteReviewKey ? suiteReview.result : null;
  const suiteRepairGroups = Object.entries((currentSuiteReview?.case_preflights ?? []).reduce<Record<string, string[]>>(
    (groups, item) => {
      item.issues.forEach((issue) => {
        groups[issue.reason_code] ??= [];
        groups[issue.reason_code].push(item.name);
      });
      return groups;
    },
    {},
  )).sort(([left], [right]) => left.localeCompare(right));
  const cases: CaseDefinition[] = loadedSuiteSnapshot?.cases ?? [];
  const fixtureOptions = (fixturesQuery.data?.fixtures ?? [])
    .filter((fixture) => fixture.status === 'active')
    .map((fixture) => {
      return {
        value: fixture.id,
        label: `${fixture.name}${fixture.scenario_label ? ` — ${fixture.scenario_label}` : ''}${
          fixture.parent_fixture_id
            ? ` · ${inputFixtureLabel(fixture)}`
            : fixture.mode === 'source_lineage_with_input_overrides'
              ? ` · ${fixture.input_override_count} input · ${fixture.trigger_override_count} trigger`
              : fixture.mode === 'synthetic_trigger_seed'
                ? ` · ${fixture.trigger_override_count} source-free trigger`
                : requiresFixtureReadiness(fixture)
                  ? ` · ${inputFixtureLabel(fixture)}`
                  : ''
        }`,
        disabled: requiresFixtureReadiness(fixture) && inputFixtureReadiness(fixture) !== 'ready',
      };
    });

  useEffect(() => {
    if (!opened) {
      setInitializedSuiteKey(null);
      setCasePlans({});
      setFixtureToRetire(null);
      setSuiteValidationError(null);
      setSuiteReview(null);
      setSuiteInitializationError(null);
      setLoadedSuiteSnapshot(null);
      setBindingCaseIndex(null);
      bindingRequestSequence.current += 1;
      return;
    }
    if (!suiteQuery.data || initializedSuiteKey === suiteKey) return;
    if (initializedSuiteKey?.startsWith(`${version.id}:`)) {
      setSuiteInitializationError(
        'The selected checks changed while this editor was open. Close and reopen it to review the latest revision before saving.',
      );
      return;
    }
    try {
      if (suiteQuery.data.authoring_snapshot === undefined) {
        throw new Error('The regression-suite authoring snapshot is unavailable. Reload after the API is updated.');
      }
      if (
        suiteQuery.data.authoring_snapshot === null
        && (suiteQuery.data.suite_id != null || suiteQuery.data.digest != null || suiteQuery.data.snapshot != null)
      ) {
        throw new Error('The saved regression suite cannot be edited because its authoring snapshot needs repair.');
      }
      const snapshot = parseVersionTestSuiteSnapshot(JSON.stringify(
        suiteQuery.data.authoring_snapshot ?? EMPTY_VERSION_TEST_SUITE_SNAPSHOT,
      ));
      setCaseDrafts(snapshot.cases.map(toVersionTestCaseDraft));
      setAdvancedSnapshotText(JSON.stringify(snapshot, null, 2));
      setSuiteInitializationError(null);
      setLoadedSuiteSnapshot(snapshot);
      setCasePlans(Object.fromEntries(Object.entries(runLaunchRequests.current.launches).map(
        ([caseId, launch]) => [caseId, launch.plan],
      )));
      setInitializedSuiteKey(suiteKey);
    } catch (error) {
      setCaseDrafts([]);
      setLoadedSuiteSnapshot(null);
      setSuiteInitializationError(
        error instanceof Error
          ? error.message
          : 'The saved regression suite has an unsupported definition.',
      );
    }
  }, [initializedSuiteKey, opened, suiteQuery.data, suiteKey, version.id]);

  useEffect(() => {
    if (!opened || testAssuranceStatus !== 'running') return;
    const intervalId = window.setInterval(() => {
      void refetchReadiness();
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [opened, refetchReadiness, testAssuranceStatus]);

  const suiteDefinitionReady = (
    initializedSuiteKey === suiteKey
    && Boolean(suiteQuery.data)
    && Boolean(loadedSuiteSnapshot)
    && !suiteQuery.isError
    && !suiteInitializationError
  );
  const directSuiteEditingReady = suiteDefinitionReady && routeInspected;

  const updateCaseDraft = (index: number, update: Partial<VersionTestCaseDraft>) => {
    setSuiteValidationError(null);
    setSuiteReview(null);
    setCaseDrafts((current) => current.map(
      (draft, currentIndex) => currentIndex === index ? { ...draft, ...update } : draft,
    ));
  };

  const toggleAdvancedEditor = () => {
    try {
      if (!showAdvancedEditor) {
        setAdvancedSnapshotText(JSON.stringify(buildVersionTestSuiteSnapshot(caseDrafts), null, 2));
      } else {
        const snapshot = parseVersionTestSuiteSnapshot(advancedSnapshotText);
        setCaseDrafts(snapshot.cases.map(toVersionTestCaseDraft));
      }
      setSuiteValidationError(null);
      setSuiteReview(null);
      setShowAdvancedEditor((current) => !current);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Cannot switch editors',
        message: error instanceof Error
          ? error.message
          : 'Fix the suite JSON before returning to the structured editor.',
      });
    }
  };

  const reviewSuite = async () => {
    if (!directSuiteEditingReady) return;
    setSuiteValidationError(null);
    setSuiteReview(null);
    let snapshot: unknown;
    try {
      snapshot = showAdvancedEditor
        ? parseVersionTestSuiteSnapshot(advancedSnapshotText)
        : buildVersionTestSuiteSnapshot(caseDrafts);
    } catch (error) {
      setSuiteValidationError(error instanceof Error ? error.message : 'Fix the suite definition before review.');
      return;
    }
    setReviewingSuite(true);
    try {
      const response = await apiClient.post<AutomationVersionTestSuitePreviewResponse>(
        preview_automation_version_test_suite_url(version.id),
        { snapshot },
      );
      setSuiteReview({ key: suiteReviewKey, result: response.data });
    } catch (error) {
      setSuiteValidationError(getHttpErrorMessage(error) || 'Could not review the suite. Try again.');
    } finally {
      setReviewingSuite(false);
    }
  };

  const save = async () => {
    const saveScope = activeSaveScope.current;
    const isCurrent = () => activeSaveScope.current === saveScope && saveScope.opened;
    if (!directSuiteEditingReady) {
      notifications.show({
        color: 'red',
        title: 'Regression suite is unavailable',
        message: 'Wait for the current checks to load successfully before saving a revision.',
      });
      return;
    }
    let snapshot: unknown;
    setSuiteValidationError(null);
    try {
      snapshot = showAdvancedEditor
        ? parseVersionTestSuiteSnapshot(advancedSnapshotText)
        : buildVersionTestSuiteSnapshot(caseDrafts);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : showAdvancedEditor
          ? 'Enter valid advanced suite JSON before saving.'
          : 'Each case needs valid declarative intent and assertion JSON.';
      setSuiteValidationError(message);
      notifications.show({ color: 'red', title: 'Invalid regression suite', message });
      return;
    }
    setSaving(true);
    setSuiteReview(null);
    try {
      const preview = await apiClient.post<AutomationVersionTestSuitePreviewResponse>(
        preview_automation_version_test_suite_url(version.id), { snapshot },
      );
      if (!isCurrent()) return;
      if (
        preview.data.selection_generation !== undefined
        && preview.data.selection_generation !== saveScope.selectionGeneration
      ) {
        throw new Error('The selected checks changed. Close and reopen the editor before saving.');
      }
      const ineligibleCases = preview.data.case_preflights.filter((item) => !item.eligible);
      if (!preview.data.all_cases_executable || ineligibleCases.length > 0) {
        setSuiteReview({ key: suiteReviewKey, result: preview.data });
        const issues = ineligibleCases.flatMap((item) => item.issues.map((issue) => `${item.name}: ${issue.message}`));
        throw new Error(issues.join(' ') || 'Regression cases need repair before this suite can be saved.');
      }
      const saved = await apiClient.put<Pick<AutomationVersionTestSuite, 'automation_version_id' | 'suite_id'>>(
        get_automation_version_test_suite_url(version.id), {
        snapshot: preview.data.normalized_snapshot, expected_suite_digest: suiteQuery.data?.digest ?? null,
        expected_selection_generation: saveScope.selectionGeneration,
      });
      if (saved.data.automation_version_id !== version.id || !saved.data.suite_id) {
        throw new Error('The check save response does not identify this executable version and revision.');
      }
      void queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSION_TEST_SUITE_KEY(automationId, version.id) });
      void queryClient.invalidateQueries({ queryKey: AUTOMATION_BUILDER_READINESS_KEY(automationId, version.id) });
      if (!isCurrent()) return;
      notifications.show({ color: 'green', title: 'Checks saved', message: 'A new check revision was saved. The executable version is unchanged.' });
      onClose();
    } catch (error) {
      if (!isCurrent()) return;
      const message = getHttpErrorMessage(error) || 'Refresh and try again.';
      setSuiteValidationError(message);
      notifications.show({ color: 'red', title: 'Could not save regression cases', message });
    } finally {
      if (isCurrent()) setSaving(false);
    }

  };

  const selectFixture = async (index: number, fixtureId: string) => {
    const selectedDraft = caseDrafts[index];
    if (!selectedDraft) return;
    setSuiteReview(null);
    let intentWithoutSubstitutions: string;
    try {
      intentWithoutSubstitutions = bindFixtureSubstitutions(selectedDraft.intentText, []);
    } catch {
      if (!fixtureId) {
        updateCaseDraft(index, { fixture_id: '' });
      }
      notifications.show({
        color: 'red',
        title: 'Fix intent JSON first',
        message: fixtureId
          ? 'The fixture was not changed because this case has invalid declarative intent JSON.'
          : 'The fixture was removed, but stale substitutions could not be cleared until the intent JSON is valid.',
      });
      return;
    }
    const bindingRequestKey = `${version.id}:${++bindingRequestSequence.current}`;
    setCaseDrafts((current) => current.map((draft, currentIndex) => currentIndex === index ? {
      ...draft,
      bindingRequestKey,
      fixture_id: fixtureId,
      intentText: fixtureId ? draft.intentText : intentWithoutSubstitutions,
    } : draft));
    if (!fixtureId) return;
    setBindingCaseIndex(index);
    try {
      const response = await apiClient.get<AutomationVersionTestFixtureBinding>(
        get_automation_version_test_fixture_binding_url(automationId, version.id, fixtureId),
      );
      if (!response.data.compatible) {
        throw new Error(response.data.issue?.message || 'This fixture is incompatible with the selected version.');
      }
      const boundIntentText = bindFixtureSubstitutions(
        selectedDraft.intentText,
        response.data.fixture_substitutions,
      );
      setCaseDrafts((current) => current.map((draft) => draft.bindingRequestKey === bindingRequestKey ? {
        ...draft,
        fixture_id: fixtureId,
        intentText: boundIntentText,
      } : draft));
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Fixture cannot be bound',
        message: getHttpErrorMessage(error) || 'Choose a fixture compatible with this exact automation version.',
      });
      setCaseDrafts((current) => current.map((draft) => draft.bindingRequestKey === bindingRequestKey ? {
        ...draft,
        fixture_id: '',
        intentText: intentWithoutSubstitutions,
      } : draft));
    } finally {
      setBindingCaseIndex(null);
    }
  };

  const createFixture = async () => {
    const requiresSourceRun = fixtureMode !== 'synthetic_trigger_seed';
    if (!fixtureName.trim() || (requiresSourceRun && !sourceRunId.trim())) {
      notifications.show({
        color: 'red',
        title: 'Test input details required',
        message: requiresSourceRun
          ? fixtureMode === 'source_lineage_with_input_overrides'
            ? 'Enter a name and fully completed source run ID.'
            : 'Enter a name and source run ID.'
          : 'Enter a name for the test inputs.',
      });
      return;
    }
    let inputOverrides: unknown[] = [];
    let triggerOverrides: unknown[] = [];
    if (fixtureMode !== 'source_lineage') {
      try {
        ({ inputOverrides, triggerOverrides } = parseSyntheticFixtureOverrides(
          fixtureMode === 'synthetic_trigger_seed' ? '[]' : inputOverridesText,
          triggerOverridesText,
        ));
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Invalid synthetic inputs',
          message: error instanceof Error ? error.message : 'Review the synthetic override JSON.',
        });
        return;
      }
      if (!confirmSyntheticNonSensitive) {
        notifications.show({ color: 'red', title: 'Confirmation required', message: 'Confirm that synthetic values contain no secrets or sensitive customer data.' });
        return;
      }
    }
    setCreatingFixture(true);
    try {
      await apiClient.post(get_automation_test_fixtures_url(automationId), {
        name: fixtureName.trim(),
        scenario_label: scenarioLabel.trim(),
        source_run_id: fixtureMode === 'synthetic_trigger_seed' ? null : sourceRunId.trim(),
        mode: fixtureMode,
        created_against_version_id: fixtureMode === 'source_lineage' ? null : version.id,
        input_overrides: fixtureMode === 'source_lineage_with_input_overrides' ? inputOverrides : [],
        trigger_overrides: fixtureMode === 'source_lineage' ? [] : triggerOverrides,
        confirm_synthetic_non_sensitive: fixtureMode !== 'source_lineage' && confirmSyntheticNonSensitive,
      });
      setFixtureName(''); setScenarioLabel(''); setSourceRunId('');
      setFixtureMode('source_lineage'); setInputOverridesText('[]'); setTriggerOverridesText('[]');
      setConfirmSyntheticNonSensitive(false);
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_TEST_FIXTURES_KEY(automationId) });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Could not record test inputs',
        message: getHttpErrorMessage(error) || (fixtureMode === 'source_lineage'
          ? 'Review the source run and its ready trigger inputs.'
          : fixtureMode === 'source_lineage_with_input_overrides'
            ? 'Review the fully completed source run and synthetic input definition.'
            : 'Review the Manual Trigger JSON input definition.'),
      });
    } finally { setCreatingFixture(false); }
  };

  const previewFixtureInputs = async () => {
    let inputOverrides: unknown[];
    let triggerOverrides: unknown[];
    try {
      ({ inputOverrides, triggerOverrides } = parseSyntheticFixtureOverrides(
        fixtureMode === 'synthetic_trigger_seed' ? '[]' : inputOverridesText,
        triggerOverridesText,
      ));
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Invalid synthetic inputs',
        message: error instanceof Error ? error.message : 'Review the synthetic override JSON.',
      });
      return;
    }
    setPreflightingFixtureInputs(true);
    try {
      const response = await apiClient.post<AutomationTestFixtureInputOverridesPreviewResponse>(
        preview_automation_test_fixture_inputs_url(automationId, version.id),
        {
          fixture_mode: fixtureMode,
          input_overrides: fixtureMode === 'synthetic_trigger_seed' ? [] : inputOverrides,
          trigger_overrides: triggerOverrides,
        },
      );
      if (response.data.valid) {
        notifications.show({
          color: 'green',
          title: 'Synthetic inputs are valid',
          message: `The current version accepts ${response.data.input_overrides.length} input and ${response.data.trigger_overrides.length} Manual Trigger override${response.data.trigger_overrides.length === 1 ? '' : 's'}. No fixture or dataset was created.`,
        });
      } else {
        notifications.show({
          color: 'red',
          title: 'Synthetic inputs need correction',
          message: response.data.diagnostics.map((diagnostic: AutomationTestFixtureInputDiagnostic) => diagnostic.message).join(' ')
            || 'Review the selected slots and input values.',
        });
      }
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Could not validate synthetic inputs',
        message: getHttpErrorMessage(error) || 'Review the selected slots and input values.',
      });
    } finally {
      setPreflightingFixtureInputs(false);
    }
  };

  const confirmFixtureRetirement = () => {
    if (!fixtureToRetire) return;
    retireFixture.mutate(
      { fixtureId: fixtureToRetire.id },
      {
        onSuccess: () => {
          notifications.show({
            color: 'green',
            title: 'Fixture retired',
            message: 'Historical evidence is preserved. Referencing suites now require repair.',
          });
          setFixtureToRetire(null);
          setCasePlans({});
        },
        onError: (error) => {
          notifications.show({
            color: 'red',
            title: 'Could not retire fixture',
            message: getHttpErrorMessage(error) || 'Refresh fixture state and try again.',
          });
        },
      },
    );
  };

  const planCase = async (testCase: CaseDefinition, verificationContext?: VerificationPreparation): Promise<VerificationPreparationResult> => {
    if (launchInFlight.current || preparationInFlight.current) return { status: 'not_prepared' };
    const pendingLaunch = runLaunchRequests.current.launches[testCase.id];
    if (pendingLaunch) {
      setCasePlans((current) => ({ ...current, [testCase.id]: pendingLaunch.plan }));
      notifications.show({
        color: 'yellow',
        title: 'Confirm the earlier launch first',
        message: `Retry request ${pendingLaunch.requestId} or inspect recent Run evidence before preparing another plan.`,
      });
      return { status: 'not_prepared' };
    }
    setCasePlans((current) => ({ ...current, [testCase.id]: undefined }));
    if (
      !suiteQuery.data?.latest_version_id
      || suiteQuery.data.head_revision == null
    ) {
      notifications.show({
        color: 'red',
        title: 'Could not prepare regression run',
        message: 'Refresh the regression suite to load its exact automation-head fences.',
      });
      return { status: 'not_prepared' };
    }
    preparationInFlight.current = true;
    setPlanningCaseId(testCase.id);
    try {
      const plan = await apiClient.post(
        create_automation_version_test_case_plan_url(automationId, version.id, testCase.id),
        {
          expected_latest_version_id: suiteQuery.data.latest_version_id,
          expected_published_version_id: suiteQuery.data.published_version_id ?? null,
          expected_head_revision: suiteQuery.data.head_revision,
          ...verificationContext,
        },
      );
      setCasePlans((current) => ({ ...current, [testCase.id]: plan.data }));
      return { status: 'prepared' };
    } catch (error) {
      if (verificationContext && isVerificationSelectionChanged(error)) {
        return { status: 'selection_stale', message: getHttpErrorMessage(error) };
      }
      notifications.show({ color: 'red', title: 'Could not prepare regression run', message: getHttpErrorMessage(error) || 'Review the case definition and try again.' });
      return { status: 'not_prepared' };
    } finally {
      preparationInFlight.current = false;
      setPlanningCaseId(null);
    }
  };

  const runSafeCase = async (testCase: CaseDefinition) => {
    if (launchInFlight.current || preparationInFlight.current) return;
    const priorLaunch = runLaunchRequests.current.launches[testCase.id];
    const plan = priorLaunch?.plan ?? casePlans[testCase.id];
    if (!plan?.plan_token) return;
    launchInFlight.current = true;
    const launch = priorLaunch ?? { plan, requestId: crypto.randomUUID() };
    runLaunchRequests.current.launches[testCase.id] = launch;
    writePendingLaunches(launchStorageKey, runLaunchRequests.current.launches);
    setRunningCaseId(testCase.id);
    try {
      const response = await apiClient.post(
        create_automation_version_test_case_run_from_plan_url(automationId, version.id, testCase.id),
        { plan_token: launch.plan.plan_token, request_id: launch.requestId, confirm_run: true },
      );
      const createdId = typeof response.data?.id === 'string' && response.data.id.trim()
        ? response.data.id
        : undefined;
      const preparationUnobserved = !createdId || !response.data?.liveness
        || response.data.liveness.reason_code === 'builder_test_launch_preparation_unobserved';
      if (preparationUnobserved) {
        launch.runId = createdId ?? launch.runId;
      } else {
        delete runLaunchRequests.current.launches[testCase.id];
      }
      writePendingLaunches(launchStorageKey, runLaunchRequests.current.launches);
      notifications.show({
        color: preparationUnobserved ? 'yellow' : 'green',
        title: preparationUnobserved ? 'Regression launch needs attention' : 'Regression run created',
        message: preparationUnobserved
          ? `${launch.runId ? `Run ${launch.runId} may exist.` : 'A run may already exist.'} Launch is unconfirmed; retry the same request or inspect run history before another launch.`
          : `Run ${createdId} is tracked for ${testCase.name}. Check its status for execution progress.`,
      });
      void Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: AUTOMATION_BUILDER_READINESS_KEY(automationId, version.id) }),
        queryClient.invalidateQueries({
          queryKey: AUTOMATION_VERSION_TEST_CASE_RUNS_KEY(automationId, version.id, testCase.id),
        }),
      ]);
    } catch (error) {
      const rejected = axios.isAxiosError(error) && error.response?.status !== undefined
        && error.response.status < 500;
      notifications.show({
        color: 'red',
        title: rejected ? 'Regression launch was rejected' : 'Could not confirm regression launch',
        message: `${getHttpErrorMessage(error) || 'Check the earlier request before another launch.'} Request ID: ${launch.requestId}`,
      });
    } finally {
      launchInFlight.current = false;
      setRunningCaseId(null);
    }
  };

  return <StyledModal opened={opened} onClose={onClose} title={`Diagnostics and regressions · ${getAutomationVersionLabel(version)}`} size="xl">
    <Tabs value={mode} onChange={setMode}>
    <Stack gap="md">
      <Tabs.List>
        <Tabs.Tab value="diagnostic">Diagnostic run</Tabs.Tab>
        <Tabs.Tab value="regression">Saved regression cases</Tabs.Tab>
      </Tabs.List>
      <Alert color="blue" title="Separate running from saving">A diagnostic creates a safe run without changing this version. Saving regression cases creates a check revision without changing this executable version. Recording test inputs changes neither.</Alert>
      {opened && <VerificationPanel
        key={`${automationId}:${version.id}`}
        automationId={automationId}
        versionId={version.id}
        preparationInFlight={planningCaseId !== null || runningCaseId !== null}
        onPrepareCase={async (caseId, verificationContext) => {
          const selected = cases.find((testCase) => testCase.id === caseId);
          if (selected) { setMode('regression'); return planCase(selected, verificationContext); }
          else notifications.show({ color: 'yellow', title: 'Refresh saved cases', message: 'The recommended case is not in the loaded selection. Refresh before preparing it.' });
          return { status: 'not_prepared' };
        }}
        onLibraryChanged={() => {
          void queryClient.invalidateQueries({ queryKey: AUTOMATION_BUILDER_READINESS_KEY(automationId, version.id) });
          void queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSION_TEST_SUITE_KEY(automationId, version.id) });
          void queryClient.invalidateQueries({ queryKey: AUTOMATION_TEST_FIXTURES_KEY(automationId) });
        }}
      />}
      <Stack gap="xs">
        <Text fw={600} size="sm">Recorded test inputs</Text>
        {fixturesQuery.isPending && <Text size="sm" c="dimmed">Loading recorded test inputs…</Text>}
        {fixturesQuery.isError && (
          <Alert color="red" title="Could not load recorded test inputs">
            {getHttpErrorMessage(fixturesQuery.error) || 'Refresh before recording or selecting test inputs.'}
          </Alert>
        )}
        {fixturesQuery.data?.fixtures.map((fixture) => (
          <Group key={fixture.id} justify="space-between" align="center">
            <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
              <Text size="sm">
                <Code>{fixture.id}</Code> · {fixture.name}
                {fixture.scenario_label ? ` — ${fixture.scenario_label}` : ''}
                {fixture.parent_fixture_id
                  ? ` · ${inputFixtureLabel(fixture)}`
                  : fixture.mode === 'source_lineage_with_input_overrides'
                    ? ` · ${fixture.input_override_count} synthetic input${fixture.input_override_count === 1 ? '' : 's'} · ${fixture.trigger_override_count} trigger override${fixture.trigger_override_count === 1 ? '' : 's'}`
                    : fixture.mode === 'synthetic_trigger_seed'
                      ? ` · ${fixture.trigger_override_count} source-free trigger input${fixture.trigger_override_count === 1 ? '' : 's'}`
                      : requiresFixtureReadiness(fixture)
                        ? ` · ${inputFixtureLabel(fixture)}`
                        : ''}
                {fixture.status === 'retired' ? ' (retired)' : ''}
              </Text>
              {fixture.parent_fixture_id && (
                <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                  Derived from <Code>{fixture.parent_fixture_id}</Code>. Fresh test evidence is required.
                </Text>
              )}
              {fixture.derivation_input_manifest?.map((input) => (
                <Text key={input.source_ref} size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                  <Code>{input.source_ref}</Code> →{' '}
                  <Code>{input.binding.kind === 'trigger'
                    ? `${input.binding.trigger_step_id} / ${input.binding.trigger_id}`
                    : `${input.binding.consumer_path} / ${input.binding.input_group_key}[${input.binding.input_index}]`}</Code>
                  {' · '}{input.evidence.kind} · {derivedInputOriginLabel(input)}
                </Text>
              ))}
              {fixture.mode === 'trigger_input_bundle' && fixture.trigger_input_manifest?.map((input) => (
                <Text key={`${input.trigger_step_id}:${input.trigger_id}`} size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                  <Code>{input.trigger_step_id}</Code> / <Code>{input.trigger_id}</Code>
                  {' · '}Origin: {input.origin} · {input.kind} · {input.dataset_processing_status}
                </Text>
              ))}
              {fixture.mode === 'provided_file_seed' && fixture.file_seed_manifest?.provided_source_ref && (
                <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                  Source reference: {fixture.file_seed_manifest.provided_source_ref}
                </Text>
              )}
            </Stack>
            {fixture.status === 'active' && (
              <StyledButton
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => setFixtureToRetire(fixture)}
              >
                Retire
              </StyledButton>
            )}
          </Group>
        ))}
        {fixturesQuery.hasNextPage && (
          <StyledButton
            variant="subtle"
            loading={fixturesQuery.isFetchingNextPage}
            onClick={() => void fixturesQuery.fetchNextPage()}
          >
            Load more test inputs
          </StyledButton>
        )}
        {!fixturesQuery.isPending && !fixturesQuery.isError && !fixturesQuery.hasNextPage && !fixturesQuery.data?.fixtures.length && (
          <Text size="sm" c="dimmed">No test inputs have been recorded yet.</Text>
        )}
        {fixtureToRetire && (
          <Alert color="orange" title={`Retire ${fixtureToRetire.name}?`}>
            <Stack gap="xs">
              <Text size="sm">
                Retirement is permanent. Historical evidence remains available. Inputs used by active checks or pinned releases must be removed from those dependencies before they can be retired.
              </Text>
              <Group>
                <StyledButton
                  color="red"
                  size="sm"
                  loading={retireFixture.isPending}
                  onClick={confirmFixtureRetirement}
                >
                  Retire test inputs
                </StyledButton>
                <StyledButton
                  variant="subtle"
                  size="sm"
                  disabled={retireFixture.isPending}
                  onClick={() => setFixtureToRetire(null)}
                >
                  Cancel
                </StyledButton>
              </Group>
            </Stack>
          </Alert>
        )}
        <Group align="end">
          <TextInput label="Test inputs name" value={fixtureName} onChange={(event) => setFixtureName(event.currentTarget.value)} />
          {fixtureMode !== 'synthetic_trigger_seed' && (
            <TextInput label="Source run ID" value={sourceRunId} onChange={(event) => setSourceRunId(event.currentTarget.value)} />
          )}
          <TextInput label="Scenario label" value={scenarioLabel} onChange={(event) => setScenarioLabel(event.currentTarget.value)} />
          <Select
            label="Test inputs mode"
            data={[
              { value: 'source_lineage', label: 'Recorded run inputs' },
              { value: 'source_lineage_with_input_overrides', label: 'Source lineage + synthetic values' },
              { value: 'synthetic_trigger_seed', label: 'Source-free Manual Trigger JSON' },
            ]}
            value={fixtureMode}
            onChange={(value) => {
              if (
                value === 'source_lineage_with_input_overrides'
                || value === 'synthetic_trigger_seed'
              ) {
                setFixtureMode(value);
              } else {
                setFixtureMode('source_lineage');
              }
            }}
          />
          {fixtureMode !== 'source_lineage' && (
            <StyledButton
              variant="subtle"
              onClick={() => void previewFixtureInputs()}
              loading={preflightingFixtureInputs}
              disabled={creatingFixture}
            >
              Validate synthetic values
            </StyledButton>
          )}
          <StyledButton
            onClick={() => void createFixture()}
            loading={creatingFixture}
            disabled={preflightingFixtureInputs || fixturesQuery.isPending || fixturesQuery.isError}
          >
            Record test inputs
          </StyledButton>
        </Group>
        <Text size="xs" c="dimmed">
          {fixtureMode === 'source_lineage'
            ? 'Reuse ready trigger inputs from an eligible operational run of this automation: completed, completed with errors, paused for user input, failed, or cancelled. Run status does not establish expected test results.'
            : fixtureMode === 'source_lineage_with_input_overrides'
              ? 'Synthetic overlays require a fully completed source run from this automation. Review the expected test results separately.'
              : 'Supply schema-validated, non-sensitive Manual Trigger JSON for this version without a source run. Review the expected test results separately.'}
        </Text>
        {fixtureMode !== 'source_lineage' && (
          <SyntheticFixtureAuthoringForm
            allowConfiguredInputs={fixtureMode === 'source_lineage_with_input_overrides'}
            candidates={fixtureInputsQuery.data?.candidates ?? []}
            ineligibleInputs={fixtureInputsQuery.data?.ineligible_inputs ?? []}
            triggerCandidates={fixtureInputsQuery.data?.trigger_candidates ?? []}
            ineligibleTriggerInputs={fixtureInputsQuery.data?.ineligible_trigger_inputs ?? []}
            isLoading={fixtureInputsQuery.isLoading}
            error={fixtureInputsQuery.error}
            inputOverridesValue={inputOverridesText}
            onInputOverridesChange={setInputOverridesText}
            triggerOverridesValue={triggerOverridesText}
            onTriggerOverridesChange={setTriggerOverridesText}
            confirmedNonSensitive={confirmSyntheticNonSensitive}
            onConfirmedNonSensitiveChange={setConfirmSyntheticNonSensitive}
          />
        )}
      </Stack>
      <Tabs.Panel value="diagnostic">
      {mode === 'diagnostic' && <DiagnosticRunPanel
        key={version.id}
        automationId={automationId}
        versionId={version.id}
        fences={suiteQuery.data}
        fixtureOptions={fixtureOptions}
        canScaffold={directSuiteEditingReady}
        onScaffold={(testCase) => {
          try {
            const drafts = showAdvancedEditor
              ? parseVersionTestSuiteSnapshot(advancedSnapshotText).cases.map(toVersionTestCaseDraft)
              : caseDrafts;
            if (drafts.some((draft) => draft.id === testCase.id)) {
              throw new Error('This regression case ID is already present. Choose another ID before preparing it.');
            }
            setCaseDrafts([...drafts, toVersionTestCaseDraft(testCase)]);
            setShowAdvancedEditor(false);
            setSuiteReview(null);
            setMode('regression');
          } catch (error) {
            throw new Error(error instanceof Error ? error.message : 'Could not add this regression case.');
          }
        }}
      />}
      </Tabs.Panel>
      <Tabs.Panel value="regression">
      <Stack gap="md">
      {mode === 'regression' && <>
      {readinessQuery.isError && (
        <Alert color="red" title="Could not load current assurance">
          {getHttpErrorMessage(readinessQuery.error) || 'Refresh before relying on this version’s assurance status.'}
        </Alert>
      )}
      {readinessQuery.data?.test_assurance && <Stack gap={2}>
        <Text fw={600} size="sm">Current assurance: {readinessQuery.data.test_assurance.status.replaceAll('_', ' ')}</Text>
        {readinessQuery.data.test_assurance.message && <Text size="sm" c="dimmed">{readinessQuery.data.test_assurance.message}</Text>}
        {readinessQuery.data.test_assurance.cases.map((testCase) => {
          const integrityLabel = formatExecutionIntegrityDisposition(testCase.execution_integrity_disposition);
          return <Text key={testCase.case_id} size="sm" c={testCase.status === 'passed' ? 'green' : 'dimmed'}>
            {testCase.name}: Overall: {testCase.status.replaceAll('_', ' ')}
            {testCase.latest_run_status
              ? ` · Runtime: ${testCase.latest_run_status.replaceAll('_', ' ')}`
              : ''}
            {integrityLabel ? ` · Integrity: ${integrityLabel}` : ''}
            {testCase.assertion_status
              ? ` · Assertions: ${testCase.assertion_status.replaceAll('_', ' ')}`
              : ''}
            {testCase.proof_surface === 'retained_execution_reevaluation'
              ? ' · Assertions reevaluated on the retained execution'
              : ''}
            {' — '}{testCase.message}
          </Text>;
        })}
      </Stack>}
      {cases.length > 0 && <>
        <Divider />
        <Stack gap="xs">
          <Text fw={600} size="sm">Run saved regression cases</Text>
          {cases.map((testCase) => {
            const plan = casePlans[testCase.id];
            const execution = plan?.execution_slice;
            return <Stack key={testCase.id} gap={4}>
              <Group justify="space-between"><Text size="sm">{testCase.name}</Text><StyledButton size="sm" onClick={() => void planCase(testCase)} loading={planningCaseId === testCase.id} disabled={runningCaseId !== null || planningCaseId !== null}>Prepare regression run</StyledButton></Group>
              {testCase.description && <Text size="xs" c="dimmed">{testCase.description}</Text>}
              <Text size="xs" c="dimmed">Target steps: {Array.isArray(testCase.intent.target_step_ids) ? testCase.intent.target_step_ids.join(', ') : 'Invalid intent'}</Text>
              <CaseHistory versionId={version.id} caseId={testCase.id} />
              {runLaunchRequests.current.launches[testCase.id] && (
                <Alert color="yellow" title="Earlier launch is unconfirmed">
                  <Text size="xs">Request <Code>{runLaunchRequests.current.launches[testCase.id].requestId}</Code> may already have created a Run. Retry the same request or inspect its status before preparing another plan.</Text>
                  {runLaunchRequests.current.launches[testCase.id].runId && (
                    <Text size="xs" component="a" href={`/automations/${automationId}/runs/${runLaunchRequests.current.launches[testCase.id].runId}`}>
                      Inspect Run {runLaunchRequests.current.launches[testCase.id].runId}
                    </Text>
                  )}
                  <StyledButton size="sm" variant="subtle" disabled={runningCaseId !== null} onClick={() => {
                    if (launchInFlight.current) return;
                    delete runLaunchRequests.current.launches[testCase.id];
                    writePendingLaunches(launchStorageKey, runLaunchRequests.current.launches);
                    setCasePlans((current) => ({ ...current, [testCase.id]: undefined }));
                  }}>Clear this request after checking its Run status</StyledButton>
                </Alert>
              )}
              {plan && <Alert color={plan.eligible ? 'blue' : 'red'} title={plan.eligible ? 'Ready to run — no execution yet' : 'Plan is not eligible'}>
                <TestPlanAnalysisSummary analysis={execution} issues={plan.issues} />
                <Text size="xs">
                  {execution
                    ? `${execution.scheduled_step_ids.length} scheduled steps · ${execution.blocked_effect_step_ids.length} blocked effects`
                    : 'Execution scope is unavailable for this plan.'}
                </Text>
                {execution?.fixture_source_lineage_sha256 && (
                  <Text size="xs">
                    Source lineage <Code>{execution.fixture_source_lineage_sha256}</Code>
                  </Text>
                )}
                {execution?.fixture_definition_sha256 && (
                  <Text size="xs">
                    Test input definition <Code>{execution.fixture_definition_sha256}</Code>
                  </Text>
                )}
                {!plan.eligible && plan.issues?.map((issue, index) => (
                  <Text key={`${issue.reason_code}-${index}`} size="xs">{issue.message}</Text>
                ))}
                <FixtureDependencyDiagnostics diagnostics={plan.diagnostics} />
                {plan.eligible && plan.plan_token ? <StyledButton size="sm" mt="xs" onClick={() => void runSafeCase(testCase)} loading={runningCaseId === testCase.id} disabled={planningCaseId !== null || (runningCaseId !== null && runningCaseId !== testCase.id)}>Run regression case — external actions blocked</StyledButton> : <Text size="xs" mt="xs">Ineligible plans are not started from this panel.</Text>}
              </Alert>}
            </Stack>;
          })}
        </Stack>
      </>}
      <Divider />
      <Stack gap="xs">
        <Group justify="space-between">
          <div><Text fw={600} size="sm">Regression-suite definition</Text><Text size="xs" c="dimmed">Saving these reusable definitions creates a check revision; it does not change the executable version or run the checks. Review test inputs, steps, expected results, and assurance requirements before saving.</Text></div>
          <StyledButton variant="subtle" size="sm" onClick={toggleAdvancedEditor} disabled={!suiteDefinitionReady || bindingCaseIndex !== null}>{showAdvancedEditor ? 'Use structured editor' : 'Advanced JSON'}</StyledButton>
        </Group>
        {!routeInspected && (
          exactVersion.isError ? <Alert color="red" title="Could not inspect the suite save route">
            Refresh the exact implementation before editing regression cases.
            <StyledButton variant="subtle" onClick={() => void exactVersion.refetch()}>Retry implementation inspection</StyledButton>
          </Alert> : <Text size="sm" c="dimmed">Inspecting the exact implementation before enabling suite editing…</Text>
        )}
        {(suiteQuery.isPending || (suiteQuery.data && initializedSuiteKey !== suiteKey && !suiteInitializationError)) && (
          <Text size="sm" c="dimmed">Loading saved regression cases…</Text>
        )}
        {suiteQuery.isError && (
          <Alert color="red" title="Could not load the current regression suite">
            {getHttpErrorMessage(suiteQuery.error) || 'Refresh and try again before editing this version.'}
          </Alert>
        )}
        {suiteInitializationError && (
          <Alert color="red" title="Saved regression suite is not supported">
            {suiteInitializationError}
          </Alert>
        )}
        {suiteValidationError && (
          <Alert color="red" title="Regression suite needs correction">
            {suiteValidationError}
          </Alert>
        )}
        <fieldset disabled={!directSuiteEditingReady} className={classes.definition}>
        <Stack gap="xs">
        {suiteDefinitionReady && (showAdvancedEditor ? <>
          <Alert color="yellow" title="Advanced suite editing">
            Use this only for supported fields that do not yet have a structured control. The server remains the
            authority for declarative intent, fixture compatibility, assertions, and scheduler admission.
          </Alert>
          <Textarea
            label="Advanced regression-suite JSON"
            value={advancedSnapshotText}
            onChange={(event) => {
              setSuiteValidationError(null);
              setSuiteReview(null);
              setAdvancedSnapshotText(event.currentTarget.value);
            }}
            autosize
            minRows={14}
            styles={{ input: { fontFamily: 'monospace' } }}
          />
        </> : <>
          {caseDrafts.map((draft, index) => <Stack key={`${draft.id}-${index}`} gap="xs" p="sm" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
            <Group justify="space-between"><Text fw={600} size="sm">Case {index + 1}</Text><StyledButton variant="subtle" size="sm" color="red" disabled={bindingCaseIndex !== null} onClick={() => {
              setSuiteValidationError(null);
              setCaseDrafts((current) => current.filter((_, currentIndex) => currentIndex !== index));
            }}>Remove case</StyledButton></Group>
            <Group grow align="end">
              <TextInput label="Case ID" required value={draft.id} onChange={(event) => updateCaseDraft(index, { id: event.currentTarget.value })} />
              <TextInput label="Case name" required value={draft.name} onChange={(event) => updateCaseDraft(index, { name: event.currentTarget.value })} />
            </Group>
            <Textarea label="Description" minRows={2} value={draft.description} onChange={(event) => updateCaseDraft(index, { description: event.currentTarget.value })} />
            <Group grow align="end">
              <Select label="Test inputs" placeholder={fixtureOptions.length ? 'Select recorded inputs' : 'Record test inputs above first'} data={fixtureOptions} value={draft.fixture_id || null} onChange={(fixture_id) => void selectFixture(index, fixture_id ?? '')} searchable required disabled={bindingCaseIndex !== null} />
              <Switch label="Required for assurance" checked={draft.required} onChange={(event) => updateCaseDraft(index, { required: event.currentTarget.checked })} />
            </Group>
            <Textarea label="Declarative scheduler intent" description="Target steps and expected behavior are reviewed here. Fixture substitutions are generated from the selected immutable fixture." value={draft.intentText} onChange={(event) => updateCaseDraft(index, { intentText: event.currentTarget.value })} autosize minRows={8} disabled={bindingCaseIndex === index} styles={{ input: { fontFamily: 'monospace' } }} />
            <Textarea label="Expected-result checks (JSON)" description="Assertions are version-bound evidence rules, not arbitrary code. JSON value paths use rows.0.amount; omit JSONPath roots ($) and brackets." value={draft.assertionSuiteText} onChange={(event) => updateCaseDraft(index, { assertionSuiteText: event.currentTarget.value })} autosize minRows={6} styles={{ input: { fontFamily: 'monospace' } }} />
          </Stack>)}
          <StyledButton variant="outline" w="fit-content" disabled={bindingCaseIndex !== null} onClick={() => {
            setSuiteValidationError(null);
            setSuiteReview(null);
            setCaseDrafts((current) => [...current, createVersionTestCaseDraft()]);
          }}>Add regression case</StyledButton>
        </>)}
        </Stack>
        </fieldset>
      </Stack>
      {suiteDefinitionReady && <StyledButton variant="outline" w="fit-content" onClick={() => void reviewSuite()} loading={reviewingSuite} disabled={!directSuiteEditingReady || saving || bindingCaseIndex !== null}>Check regression cases can run</StyledButton>}
      {currentSuiteReview && <Alert color={currentSuiteReview.all_cases_executable ? 'blue' : 'yellow'} title={currentSuiteReview.all_cases_executable ? 'Cases ready to run — no execution yet' : 'Cases need repair'}>
        <Text size="sm">{currentSuiteReview.case_preflights.length} cases · {currentSuiteReview.case_preflights.filter((item) => !item.eligible).length} need repair</Text>
        {suiteRepairGroups.map(([reasonCode, names]) => (
          <Text size="xs" key={reasonCode}>{reasonCode}: {Array.from(new Set(names)).join(', ')}</Text>
        ))}
        {currentSuiteReview.case_preflights.map((item) => <Stack key={item.case_id} gap={1} mt="xs">
          <Text size="sm" fw={600}>{item.name} · {item.required ? 'required' : 'optional'} · {item.eligible ? 'eligible' : 'needs repair'}</Text>
          <TestPlanAnalysisSummary analysis={item.execution} issues={item.issues} />
          <Text size="xs">{item.execution && item.execution.digest !== ''
            ? `${item.execution.scheduled_step_ids.length} scheduled steps · ${item.execution.blocked_effect_step_ids.length} blocked effects`
            : 'Execution scope is unavailable for this plan.'}</Text>
          {item.issues.map((issue, index) => <Text size="xs" key={`${issue.reason_code}-${index}`}>{issue.reason_code}: {issue.message}</Text>)}
          {item.warnings?.map((warning, index) => <Text size="xs" key={`${warning.reason_code}-${index}`}>Review: {warning.message}</Text>)}
        </Stack>)}
      </Alert>}
      <ModalConfirmationButtons
        onClose={onClose}
        onConfirm={save}
        confirmText="Save check revision"
        confirmDisabled={!directSuiteEditingReady || bindingCaseIndex !== null}
        confirmLoading={saving}
        closeOnConfirm={false}
      />
      </>}
      </Stack>
      </Tabs.Panel>
    </Stack>
    </Tabs>
  </StyledModal>;
}
